import createRetriableFunction from '../utils/createRetriableFunction';
import getAttachmentDownloadToken from 'owa-service/lib/operation/getAttachmentDownloadTokenOperation';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { DatapointStatus, VerboseDatapoint } from 'owa-analytics';
import { getUserMailboxInfo } from 'owa-client-ids';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isStringNullOrWhiteSpace } from 'owa-strings';
import { trace } from 'owa-trace';

const TIME_TO_WAIT_BEFORE_REFRESHING_TOKEN_IN_CASE_OF_ERROR_IN_MS = 1000 * 30; // 30 Seconds

// This will retry indefinitely and will wait 30 seconds between each retry
const createRetriableForRefreshingToken = createRetriableFunction({
    timeBetweenRetryInMS: () => TIME_TO_WAIT_BEFORE_REFRESHING_TOKEN_IN_CASE_OF_ERROR_IN_MS,
});

// TODO: The DownloadTokenGetter should be refactored so that it does not use any class but instead is just functions and module variables
class DownloadTokenGetter {
    private useDownloadUrlBase: boolean;
    private downloadToken: string;
    private isRefreshingDownloadToken: boolean;

    private static instance: DownloadTokenGetter;

    private constructor(
        private downloadTokenRefreshMinutes: number,
        private downloadUrlBase: string,
        private mailboxAddress: string,
        firstDownloadToken: string
    ) {
        // Disable the feature unless all of the pre-requisites are present.
        this.useDownloadUrlBase =
            !isStringNullOrWhiteSpace(this.downloadUrlBase) &&
            !isStringNullOrWhiteSpace(this.mailboxAddress) &&
            isFeatureEnabled('doc-attachment-downloadDomain');

        this.downloadToken = firstDownloadToken;

        if (this.downloadTokenRefreshMinutes < 1) {
            this.downloadTokenRefreshMinutes = 1;
        }

        if (this.useDownloadUrlBase) {
            setInterval(
                this.refreshDownloadTokenAndRetryInCaseOfFailure,
                1000 * 60 * this.downloadTokenRefreshMinutes
            );
        }
    }

    static getInstance(): DownloadTokenGetter {
        if (!DownloadTokenGetter.instance) {
            const userConfiguration = getUserConfiguration();

            const mailboxId = getUserMailboxInfo().mailboxId;
            const applicationSettings = userConfiguration.ApplicationSettings;

            DownloadTokenGetter.instance = new DownloadTokenGetter(
                applicationSettings.DownloadTokenRefreshMinutes,
                applicationSettings.DownloadUrlBase,
                mailboxId,
                applicationSettings.FirstDownloadToken
            );
        }

        return DownloadTokenGetter.instance;
    }

    static resetInstanceForTesting() {
        DownloadTokenGetter.instance = null;
    }

    getDownloadToken(): string {
        return this.downloadToken;
    }

    getUseDownloadUrlBase(): boolean {
        return this.useDownloadUrlBase;
    }

    getDownloadUrlBase(): string {
        return this.downloadUrlBase;
    }

    private refreshDownloadTokenAndRetryInCaseOfFailure = async (): Promise<void> => {
        async function refreshDownloadToken(): Promise<string> {
            try {
                return await getAttachmentDownloadToken();
            } catch (error) {
                trace.warn(`Unable to get the download token ${error}`);
                throw error;
            }
        }

        if (!this.isRefreshingDownloadToken) {
            this.isRefreshingDownloadToken = true;

            let totalTries = 0;
            const { retriableFunc } = createRetriableForRefreshingToken(async () => {
                totalTries++;

                this.downloadToken = await refreshDownloadToken();
                this.isRefreshingDownloadToken = false;
            });
            const performanceDatapoint = new VerboseDatapoint('AttachmentRefreshDownloadToken');

            try {
                await retriableFunc();
                performanceDatapoint.addCustomData([totalTries]);
                performanceDatapoint.end();
            } catch (error) {
                trace.warn(`Retry to refresh token failed: ${error}`);
                performanceDatapoint.addCustomData([totalTries]);
                performanceDatapoint.endWithError(DatapointStatus.ServerError, error);

                this.isRefreshingDownloadToken = false;
            }
        }
    };
}

export default function getDownloadToken(): string {
    return DownloadTokenGetter.getInstance().getDownloadToken();
}

export function getUseDownloadUrlBase(): boolean {
    return DownloadTokenGetter.getInstance().getUseDownloadUrlBase();
}

export function getDownloadUrlBase(): string {
    return DownloadTokenGetter.getInstance().getDownloadUrlBase();
}

export function resetInstanceForTesting() {
    return DownloadTokenGetter.resetInstanceForTesting();
}
